// ============================================================
// Add Protocol v3 wizard - shell components (T4 / D3 contract)
//
// Every screen inherits this scaffold; no per-screen improvisation:
//   [Back] [vial chips] [Close X]      <- WalkthroughV2 header pattern
//   step dots (current vial section)
//   "So far" rail (ONE collapsed line, tap = bottom sheet)
//   mono eyebrow -> display-800 question -> input area
//   "Not sure?" -> bottom sheet (AnnouncementModal pattern #10)
//   pinned Next CTA (typed-input screens only)
//
// Motion budget (D11): answer-card selection feedback, chip tick,
// sheet slide-up, save celebration - all honoring reduced motion.
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { formatTime } from '../../../utils/schedule';
import type { WizardScreen, WizardState } from './wizardTypes';
import { sectionSteps } from './wizardReducer';
import { mixDrawUnits } from './buildProtocol';
import { formatUnits } from '../../../utils/calculations';
import { FREQUENCY_LABELS } from '../../../data/types';
import { trackEvent } from '../../../utils/analytics';

// ------------------------------------------------------------
// Icons (inline, stroke-current)
// ------------------------------------------------------------

export const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TickIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" />
  </svg>
);

// ------------------------------------------------------------
// Vial chips (multi-vial plans only; not tappable in v3)
// ------------------------------------------------------------

export const VialChips: React.FC<{ state: WizardState }> = ({ state }) => {
  const count = state.vialCount ?? 0;
  if (count < 2) return null;
  // 320pt rule (D13): at 4 vials compress to dots-with-count.
  const compress = count >= 4;
  return (
    <div className="wz-chips" aria-label={`Vial ${state.currentVialIndex + 1} of ${count}`}>
      {compress ? (
        <span className="wz-chip wz-chip-active">{state.currentVialIndex + 1}/{count}</span>
      ) : (
        state.vials.map((v, i) => {
          const done = i < state.currentVialIndex;
          const name = v.kind === 'mix'
            ? (v.recipe.find((r) => r.peptideName)?.peptideName ?? `Vial ${i + 1}`)
            : (v.peptideName ?? `Vial ${i + 1}`);
          const label = name.length > 10 ? `${name.slice(0, 10)}…` : name;
          return (
            <span
              key={v.id}
              className={`wz-chip${i === state.currentVialIndex ? ' wz-chip-active' : ''}${done ? ' wz-chip-done' : ''}`}
            >
              {done && <TickIcon />}
              {label}
            </span>
          );
        })
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Step dots for the current vial's section
// ------------------------------------------------------------

// 's4' is deliberately NOT here: it's the fast-path interstitial and
// sectionSteps() never emits it, so rendering dots there produced a
// progressbar with no active dot stuck at "1 of N". Dots hide on it.
const SECTION_SCREENS: WizardScreen[] = ['s2', 's3a', 's3b', 's5', 's5b', 's5c', 's6', 's7', 's8', 's9', 's10', 's11', 's12', 's13', 's14'];

export const StepDots: React.FC<{ state: WizardState }> = ({ state }) => {
  if (!SECTION_SCREENS.includes(state.screen)) return null;
  const steps = sectionSteps(state, state.currentVialIndex);
  const idx = steps.indexOf(state.screen);
  return (
    <div className="wz-dots" role="progressbar" aria-valuenow={Math.max(1, idx + 1)} aria-valuemax={steps.length} aria-label={`Question ${Math.max(1, idx + 1)} of ${steps.length}`}>
      {steps.map((s, i) => (
        <span key={s} className={`wz-dot${i === idx ? ' wz-dot-active' : ''}${i < idx ? ' wz-dot-done' : ''}`} />
      ))}
    </div>
  );
};

// ------------------------------------------------------------
// "So far" rail + sheet
// ------------------------------------------------------------

export function soFarLines(state: WizardState): Array<{ vialLabel: string; answers: string[] }> {
  const out: Array<{ vialLabel: string; answers: string[] }> = [];
  state.vials.forEach((v, i) => {
    const answers: string[] = [];
    if (v.kind === 'mix') {
      const parts = v.recipe.filter((r) => r.peptideName);
      if (parts.length > 0) answers.push(parts.map((r) => `${r.peptideName}${r.amountText ? ` ${r.amountText} mg` : ''}`).join(' + '));
    } else if (v.peptideName) {
      answers.push(v.peptideName);
    }
    if (v.vialMgText) answers.push(`${v.vialMgText} ${v.vialMgUnit}`);
    if (v.physicalState) answers.push(v.physicalState === 'powder' ? 'powder' : 'liquid');
    if (v.waterMlText) answers.push(`${v.waterMlText} mL water`);
    if (v.doseText) {
      if (v.kind === 'mix') {
        // A mix has ONE dose event: name the peptide the user anchored
        // on (perPeptide mode) and show the derived shared draw so the
        // band reads like the preview card, not like a bare number.
        const draw = mixDrawUnits(v);
        const drawLabel = draw > 0 ? ` (draw ${formatUnits(draw)} units)` : '';
        if (v.doseMode === 'draw') {
          answers.push(`draw ${v.doseText} ${v.doseUnit === 'ml' ? 'mL' : 'units'}`);
        } else {
          const target = v.recipe.find((r) => r.peptideId === v.dosePeptideId) ?? v.recipe[0];
          answers.push(`dose ${v.doseText} ${v.doseUnit}${target?.peptideName ? ` ${target.peptideName}` : ''}${drawLabel}`);
        }
      } else {
        answers.push(`dose ${v.doseText} ${v.doseUnit === 'ml' ? 'mL' : v.doseUnit}`);
      }
    }
    if (v.schedule.frequency) answers.push(FREQUENCY_LABELS[v.schedule.frequency]);
    if (v.schedule.timesOfDay.length > 0) answers.push(v.schedule.timesOfDay.map(formatTime).join(', '));
    if (answers.length > 0) out.push({ vialLabel: `Vial ${i + 1}`, answers });
  });
  if (state.syringeType) {
    // Self-describing label: the sticky band flattens these answers
    // into one line, where a bare "100 units" (meaning the U-100
    // syringe) read as a draw amount right after the dose (QA
    // 2026-07-08 screenshot: "dose 250 mcg · 100 units").
    out.push({ vialLabel: 'Syringe', answers: [`U-${state.syringeType.replace('U', '')} syringe`] });
  }
  return out;
}

// ------------------------------------------------------------
// Bottom sheet (pattern #10 scaffold) - used by Not-sure, So-far,
// and the free-tier gate. Focus-trapped; restores focus on close.
// ------------------------------------------------------------

export const WizardSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** teal = brand gradient (gate); plain = white sheet (help / so-far). */
  variant?: 'plain' | 'teal';
}> = ({ open, onClose, title, children, variant = 'plain' }) => {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const sheet = sheetRef.current;
    sheet?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !sheet) return;
      // Focus trap (D13): cycle within the sheet.
      const focusables = sheet.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      restoreRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="wz-sheet-backdrop" onClick={onClose}>
      <div
        ref={sheetRef}
        className={`wz-sheet${variant === 'teal' ? ' wz-sheet-teal' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wz-sheet-head">
          {title && <div className="wz-sheet-title">{title}</div>}
          <button className="wz-sheet-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className="wz-sheet-body">{children}</div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// Answer cards - the ONE selection interaction. Auto-advance ~250ms
// after selection with a pointer-events lock so a fast double-tap
// can never advance two screens (C7).
// ------------------------------------------------------------

export interface AnswerOption {
  key: string;
  label: string;
  sub?: string;
  badge?: string;
}

export const AnswerCards: React.FC<{
  options: AnswerOption[];
  selectedKey?: string | null;
  onSelect: (key: string) => void;
  ariaLabel: string;
  /** Keys that reveal in-place UI instead of navigating: no advance
   *  delay, no lock (the user keeps interacting on this screen). */
  revealKeys?: string[];
}> = ({ options, selectedKey, onSelect, ariaLabel, revealKeys }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const lockRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A re-render with new options (new screen) releases the lock.
  useEffect(() => {
    lockRef.current = false;
    setPicked(null);
  }, [ariaLabel, options.map((o) => o.key).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unmount (screen change, incl. Back during the 250ms window)
  // cancels the pending advance - a stale answer must never re-fire
  // after the user has navigated away (codex P1-7).
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const pick = (key: string) => {
    if (revealKeys?.includes(key)) {
      setPicked(key);
      onSelect(key);
      return;
    }
    if (lockRef.current) return;
    lockRef.current = true;
    setPicked(key);
    const reduced = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    timerRef.current = setTimeout(() => onSelect(key), reduced ? 0 : 250);
  };

  return (
    <div className="wz-cards" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => {
        const active = (picked ?? selectedKey) === o.key;
        return (
          <button
            key={o.key}
            role="radio"
            aria-checked={active}
            className={`wz-card${active ? ' wz-card-active' : ''}`}
            onClick={() => pick(o.key)}
          >
            <span className="wz-card-main">
              <span className="wz-card-label">{o.label}</span>
              {o.sub && <span className="wz-card-sub">{o.sub}</span>}
            </span>
            {o.badge && <span className="wz-card-badge">{o.badge}</span>}
          </button>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------
// The screen scaffold
// ------------------------------------------------------------

export const WizardScreenShell: React.FC<{
  state: WizardState;
  eyebrow?: string;
  question: string;
  lede?: string;
  children: React.ReactNode;
  /** Pinned CTA for typed-input screens. Omit on auto-advance screens. */
  next?: { label?: string; disabled?: boolean; onNext: () => void };
  notSure?: { label?: string; body: React.ReactNode; title?: string };
  onOpenSoFar: () => void;
}> = ({ state, eyebrow = 'PROTOCOL SETUP', question, lede, children, next, notSure, onOpenSoFar }) => {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // VoiceOver (D13): focus moves to the question heading on every screen
  // change, which announces it. We deliberately do NOT also put aria-live on
  // the heading - focus-move + a live region on the same element makes
  // VoiceOver announce the question twice.
  useEffect(() => {
    headingRef.current?.focus();
  }, [question]);

  // Direction B (preview-forward): the deep-teal band at the foot of
  // every screen is the live preview. It carries the running summary
  // (tap to expand the full sheet) plus the Next CTA, so the user
  // watches their protocol take shape as they answer.
  const summary = soFarLines(state).flatMap((l) => l.answers).join(' · ');
  const hasSummary = summary.length > 0;
  const showBand = hasSummary || !!next;

  return (
    <div className="wz-screen">
      <div className="wz-question-block">
        <div className="wz-eyebrow">{eyebrow}</div>
        <h1 className="wz-question" ref={headingRef} tabIndex={-1}>
          {question}
        </h1>
        {lede && <p className="wz-lede">{lede}</p>}
      </div>
      <div className="wz-input-area">{children}</div>
      {notSure && (
        <>
          <button
            className="wz-notsure"
            onClick={() => {
              setSheetOpen(true);
              void trackEvent('wizard_notsure_opened', { screen: state.screen });
            }}
          >
            {notSure.label ?? 'Not sure?'}
          </button>
          <WizardSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={notSure.title ?? 'Not sure?'}>
            {notSure.body}
          </WizardSheet>
        </>
      )}
      {showBand && (
        <div className="wz-band">
          {hasSummary ? (
            <button className="wz-band-summary" onClick={onOpenSoFar} aria-label="Review your answers so far">
              <span className="wz-band-label">So far</span>
              <span className="wz-band-text">{summary}</span>
            </button>
          ) : (
            <div className="wz-band-summary wz-band-empty">
              <span className="wz-band-label">Building your protocol</span>
              <span className="wz-band-text">Your answers show up here as you go.</span>
            </div>
          )}
          {next && (
            <button
              className="wz-band-cta"
              disabled={next.disabled}
              onClick={next.onNext}
            >
              {next.label ?? 'Next'}
              <ArrowIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
